/**
 * Базовые функции сбора информации для отладки AMO полей
 * Предоставляет низкоуровневые функции для анализа состояния и структуры
 */

import { getCurrentAmoFieldValue } from './amoFieldsCore.js';
import { findFieldContainer, getAmoAttributes } from './amoFieldsUtilsHelpers.js';

/**
 * Базовая функция для отладки с настраиваемыми параметрами
 * @param {string|number} fieldId - ID поля
 * @param {string} debugType - Тип отладки: 'state', 'html', 'field'
 * @returns {Object} Результат отладки
 */
export function baseDebugFunction(fieldId, debugType) {
    const baseInfo = {
        fieldId,
        debugType,
        timestamp: new Date().toISOString()
    };
    
    try {
        switch (debugType) {
            case 'state':
                return { ...baseInfo, ...debugStateInfo(fieldId) };
            case 'html':
                return { ...baseInfo, ...debugHTMLInfo(fieldId) };
            case 'field':
                return { ...baseInfo, ...debugFieldInfo(fieldId) };
            default:
                return { ...baseInfo, error: 'Неизвестный тип отладки' };
        }
    } catch (error) {
        return { ...baseInfo, error: error.message };
    }
}

/**
 * Получение информации о состоянии поля
 */
export function debugStateInfo(fieldId) {
    const { attributes, success } = getAmoAttributes(fieldId);
    if (!success) {
        return { error: 'AMO model attributes не доступны' };
    }
    
    const amoFields = AMOCRM.constant("account").cf || {};
    const field = amoFields[fieldId];
    
    if (!field) {
        return { error: `Поле ${fieldId} не найдено в AMO` };
    }
    
    const info = {
        fieldName: field.NAME,
        mainFieldValue: attributes[`CFV[${fieldId}]`],
        currentValue: getCurrentAmoFieldValue(fieldId),
        enumValues: {}
    };
    
    if (field.ENUMS) {
        Object.keys(field.ENUMS).forEach(enumId => {
            const enumKey = `CFV[${fieldId}][${enumId}]`;
            const enumValue = attributes[enumKey];
            const isSelected = enumValue === enumId;
            info.enumValues[enumId] = {
                key: enumKey,
                value: enumValue,
                text: field.ENUMS[enumId],
                selected: isSelected
            };
        });
    }
    
    return info;
}

/**
 * Получение информации о HTML структуре
 */
export function debugHTMLInfo(fieldId) {
    const { element: fieldContainer, success } = findFieldContainer(fieldId);
    if (!success) {
        return { error: `Контейнер поля ${fieldId} не найден` };
    }
    
    const info = {
        container: {
            className: fieldContainer.className,
            hasDropdown: !!fieldContainer.querySelector('.checkboxes_dropdown')
        },
        checkboxes: {
            all: [],
            item_checkboxes: [],
            master_checkboxes: []
        },
        title: {}
    };
    
    const allCheckboxes = fieldContainer.querySelectorAll('input[type="checkbox"]');
    allCheckboxes.forEach(checkbox => {
        const checkboxInfo = {
            name: checkbox.name,
            value: checkbox.value,
            checked: checkbox.checked,
            classes: checkbox.className
        };
        
        info.checkboxes.all.push(checkboxInfo);
        
        if (checkbox.classList.contains('js-item-checkbox')) {
            info.checkboxes.item_checkboxes.push(checkboxInfo);
        }
        
        if (checkbox.classList.contains('js-master-checkbox')) {
            info.checkboxes.master_checkboxes.push(checkboxInfo);
        }
    });
    
    const titleWrapper = fieldContainer.querySelector('.checkboxes_dropdown__title-selected');
    if (titleWrapper) {
        const titleContainer = titleWrapper.querySelector('.checkboxes_dropdown__title');
        if (titleContainer) {
            const titleItems = titleContainer.querySelectorAll('.checkboxes_dropdown__title-item');
            info.title = {
                innerHTML: titleContainer.innerHTML,
                items: Array.from(titleItems).map(item => ({
                    text: item.textContent,
                    hasDataTitleBefore: item.hasAttribute('data-title-before')
                }))
            };
        }
    }
    
    return info;
}

/**
 * Получение общей информации о поле
 */
export function debugFieldInfo(fieldId) {
    const info = {
        foundElements: [],
        possibleSelectors: [
            `[data-id="${fieldId}"]`,
            `[name="CFV[${fieldId}]"]`,
            `#cf_${fieldId}`,
            `[data-field-id="${fieldId}"]`,
            `input[data-id="${fieldId}"]`,
            `select[data-id="${fieldId}"]`,
            `textarea[data-id="${fieldId}"]`,
            `[data-field="${fieldId}"]`,
            `.field-${fieldId}`,
            `[id*="${fieldId}"]`,
            `[name*="${fieldId}"]`
        ]
    };
    
    info.possibleSelectors.forEach(selector => {
        try {
            const elements = document.querySelectorAll(selector);
            if (elements.length > 0) {
                Array.from(elements).forEach(el => {
                    info.foundElements.push({
                        selector,
                        tagName: el.tagName,
                        type: el.type,
                        id: el.id,
                        name: el.name,
                        className: el.className,
                        visible: getComputedStyle(el).display !== 'none'
                    });
                });
            }
        } catch (e) {
            console.warn('Ошибка при проверке селектора', selector, e);
        }
    });
    
    return info;
} 